<template>
  <div class="shipment-list-container">
    <ShipmentFilters :receivers="receivers" :transporters="transporters" />

    <div
      v-if="shipments.length === 0 && loaded && hasActiveFilters"
      class="has-text-centered is-size-5 is-italic mt-6"
    >
      <p>No shipments matched your search/filters.</p>
      <p><a @click.prevent="store.resetSearch">Clear filters</a></p>
    </div>

    <div v-else-if="shipments.length === 0 && loaded" class="has-text-centered empty-state">
      <h2 class="subtitle">No shipments yet</h2>
    </div>

    <table v-else class="table is-fullwidth is-striped shipment-list">
      <thead>
        <tr>
          <SortableHeader :store="store" field="visual_id">ID</SortableHeader>
          <th>Status</th>
          <th>Receiver</th>
          <th>Transporter</th>
          <th>Items</th>
          <SortableHeader :store="store" field="estimated_ship_at">Est. Ship</SortableHeader>
          <SortableHeader :store="store" field="shipped_at">Shipped</SortableHeader>
          <SortableHeader :store="store" field="created_at">Created</SortableHeader>
          <th></th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="shipment in shipments" :key="shipment.id">
          <td>
            <div class="id">
              <a :href="shipmentLink(shipment)">{{ shipment.visual_id }}</a>
            </div>
          </td>
          <td class="state"><ShipmentState :state="shipment.state" /></td>
          <td class="receiver">{{ shipment.receiver?.name ?? "—" }}</td>
          <td class="transporter">{{ shipment.transporter?.name ?? "—" }}</td>
          <td class="items">{{ shipment.items_count }}</td>
          <td class="estimated-ship">{{ formatDate(shipment.estimated_ship_at) }}</td>
          <td class="shipped">{{ formatDate(shipment.shipped_at) }}</td>
          <td class="created">{{ formatDate(shipment.created_at) }}</td>
          <td class="col-actions">
            <ShipmentActionsButton v-if="canEdit" :shipment="shipment" :confirms="confirms" />
          </td>
        </tr>
      </tbody>
    </table>

    <PagerWidget
      v-if="totalEntries > perPage"
      v-model="currentPage"
      :total-entries="totalEntries"
      :per-page="perPage"
      @update:model-value="store.loadPage($event)"
    />
  </div>
</template>

<script setup>
  import { storeToRefs } from "pinia";
  import { format, parseISO } from "date-fns";
  import PagerWidget from "./pager-widget.vue";
  import ShipmentFilters from "./shipment-filters.vue";
  import ShipmentState from "./shipment-state.vue";
  import ShipmentActionsButton from "./shipment-actions-button.vue";
  import SortableHeader from "./shared/sortable-header.vue";
  import { useShipmentSearchStore } from "./stores/shipment-search-store";
  import { useSearchList } from "./lib/use-search-list";
  import { editShipmentPath, shipmentPath } from "~/routes";

  const props = defineProps({
    userId: { type: Number, required: true },
    canEdit: { type: Boolean, default: false },
    receivers: { type: Array, default: () => [] },
    transporters: { type: Array, default: () => [] },
    initialData: { type: Object, default: null },
    sortConfig: { type: Object, required: true },
    confirms: { type: Object, required: true },
  });

  const store = useShipmentSearchStore();
  const { shipments, totalEntries, currentPage, perPage, loaded, hasActiveFilters } =
    storeToRefs(store);

  useSearchList(store, {
    userId: props.userId,
    sortConfig: props.sortConfig,
    initialData: props.initialData,
    knownIds: {
      receiverIds: props.receivers.map(({ value }) => value),
      transporterIds: props.transporters.map(({ value }) => value),
    },
  });

  function shipmentLink(shipment) {
    return shipment.state === "draft" ? editShipmentPath(shipment) : shipmentPath(shipment);
  }

  function formatDate(dateStr) {
    if (!dateStr) return "—";
    return format(parseISO(dateStr), "MMM d, yyyy");
  }
</script>

<style scoped lang="scss">
  @import "~jose_modern/css";
  @import "~jose_modern/css/inventory/mixins";

  .shipment-list-container {
    display: flex;
    flex-direction: column;
  }

  .empty-state {
    margin-top: 2rem;
  }

  table {
    .id {
      display: inline-flex;
      width: 100%;

      a {
        flex-grow: 1;
      }
    }

    tbody tr td {
      vertical-align: middle;
    }

    @include until(860px) {
      display: block;

      thead {
        display: none;
      }

      tbody {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
      }

      tbody tr {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.4rem 1rem;
        border: 1px solid $border;
        border-radius: $radius;
        padding: 0.75rem;
        background-color: $white !important;
        @include soft-shadow;

        td {
          // Some inventory classes set the width to 1% with very specific rules
          width: auto !important;
          display: flex;
          align-items: center;
          gap: 0.3rem;
          border: none;
          padding: 0.1rem 0;

          &:nth-child(1) {
            font-weight: $weight-semibold;
            width: 80% !important;
            flex-grow: 1;
            a {
              width: 100%;
            }
          }

          &:nth-child(n + 3) {
            flex-basis: 45%;
          }

          &.state {
            @media screen and (max-width: 540px) {
              flex-basis: 100%;
            }
          }

          &::before {
            color: $grey;
            font-size: 0.8em;
          }

          &.receiver::before {
            content: "Receiver";
          }

          &.transporter::before {
            content: "Transporter";
          }

          &.items::before {
            content: "Items";
          }

          &.estimated-ship::before {
            content: "Est. Ship";
          }

          &.shipped::before {
            content: "Shipped";
          }

          &.created::before {
            content: "Created";
          }
        }
      }
    }
  }
</style>
